import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import API_BASE from '../config';

const CLIENT_ID = 'b1a00492-073a-47ea-816f-4c329264a828';

function generateCodeVerifier() {
  const array = new Uint8Array(32);
  window.crypto.getRandomValues(array);
  return btoa(String.fromCharCode(...array))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

async function generateCodeChallenge(verifier) {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await window.crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

export default function Login() {
  const { login } = useAuth();
  const [step, setStep] = useState('initial');
  const [code, setCode] = useState('');
  const [codeVerifier, setCodeVerifier] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const startLogin = async () => {
    const verifier = generateCodeVerifier();
    const challenge = await generateCodeChallenge(verifier);
    setCodeVerifier(verifier);
    sessionStorage.setItem('code_verifier', verifier);

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: CLIENT_ID,
      redirect_uri: 'http://127.0.0.1:56121/callback',
      scope: 'openid profile email offline_access grok-cli:access api:access',
      code_challenge: challenge,
      code_challenge_method: 'S256',
      state: Math.random().toString(36).substring(2),
      nonce: Math.random().toString(36).substring(2),
      plan: 'generic',
      referrer: 'hermes-agent',
    });

    window.open(`https://auth.x.ai/oauth2/authorize?${params}`, '_blank');
    setStep('waiting');
  };

  const completeLogin = async () => {
    if (!code.trim()) return;
    setLoading(true);
    setError(null);
    const verifier = codeVerifier || sessionStorage.getItem('code_verifier');
    try {
      const res = await fetch(`${API_BASE}/api/auth/exchange`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: code.trim(),
          code_verifier: verifier,
          redirect_uri: 'http://127.0.0.1:56121/callback',
        }),
      });
      const data = await res.json();
      if (data.success) {
        login(data.tokens.access_token, data.tokens.refresh_token);
      } else {
        setError(data.error);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'radial-gradient(ellipse 80% 50% at 50% -20%, rgba(0,255,136,0.08) 0%, transparent 60%), #080a0e',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '28px',
      padding: '24px',
      fontFamily: "'DM Sans', sans-serif",
    }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '52px', marginBottom: '14px', animation: 'spin 8s linear infinite', display: 'inline-block' }}>◈</div>
        <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
        <div style={{ fontFamily: 'Bebas Neue, sans-serif', fontSize: '36px', letterSpacing: '5px', color: '#00ff88' }}>
          GROK STUDIO
        </div>
        <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '11px', color: 'rgba(0,255,136,0.6)', letterSpacing: '3px', marginTop: '4px' }}>
          V2
        </div>
        <div style={{ fontSize: '13px', color: '#7a8599', marginTop: '10px', fontFamily: 'Space Mono, monospace' }}>
          xAI Grok 계정으로 로그인해주세요
        </div>
      </div>

      <div style={{
        background: '#0d1117',
        border: '1px solid #1e2530',
        borderRadius: '18px',
        padding: '32px',
        width: '100%',
        maxWidth: '420px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      }}>
        {step === 'initial' && (
          <>
            <div style={{ background: 'rgba(0,255,136,0.05)', border: '1px solid rgba(0,255,136,0.12)', borderRadius: '8px', padding: '12px 14px' }}>
              <p style={{ fontSize: '12px', color: '#7a8599', fontFamily: 'Space Mono, monospace', lineHeight: '1.7' }}>
                💡 SuperGrok 계정이 필요해요.<br/>
                로그인 버튼을 누르면 xAI 인증 페이지가 열려요.
              </p>
            </div>
            <button
              onClick={startLogin}
              style={{
                padding: '13px', borderRadius: '9px', border: 'none',
                background: '#00ff88', color: '#000',
                fontFamily: 'Space Mono, monospace', fontSize: '13px', fontWeight: '700',
                cursor: 'pointer', letterSpacing: '1px', transition: 'all 0.2s',
              }}
            >
              🔑 xAI Grok 로그인
            </button>
          </>
        )}

        {step === 'waiting' && (
          <>
            <div style={{ background: 'rgba(0,255,136,0.05)', border: '1px solid rgba(0,255,136,0.12)', borderRadius: '8px', padding: '12px 14px' }}>
              <p style={{ fontSize: '12px', color: '#7a8599', fontFamily: 'Space Mono, monospace', lineHeight: '1.8' }}>
                1. 브라우저에서 Grok 계정으로 로그인<br/>
                2. Allow 클릭<br/>
                3. 표시된 코드를 복사해서 아래 붙여넣기
              </p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
              <label style={{ fontSize: '11px', fontFamily: 'Space Mono, monospace', color: '#7a8599', textTransform: 'uppercase', letterSpacing: '1px' }}>
                인증 코드
              </label>
              <textarea
                value={code}
                onChange={e => setCode(e.target.value)}
                placeholder="브라우저에서 복사한 코드를 붙여넣으세요..."
                rows={3}
                style={{
                  background: '#13181f', border: '1px solid #1e2530', borderRadius: '8px',
                  color: '#e8edf5', fontFamily: 'DM Sans, sans-serif', fontSize: '14px',
                  padding: '11px 13px', resize: 'vertical', width: '100%', outline: 'none',
                }}
              />
            </div>
            <button
              onClick={completeLogin}
              disabled={loading || !code.trim()}
              style={{
                padding: '13px', borderRadius: '9px', border: 'none',
                background: '#00ff88', color: '#000',
                fontFamily: 'Space Mono, monospace', fontSize: '13px', fontWeight: '700',
                cursor: loading || !code.trim() ? 'not-allowed' : 'pointer',
                opacity: loading || !code.trim() ? 0.4 : 1,
                letterSpacing: '1px',
              }}
            >
              {loading ? '⏳ 처리 중...' : '✅ 로그인 완료'}
            </button>
            <button
              onClick={() => { setStep('initial'); setCode(''); setError(null); }}
              style={{ background: 'none', border: 'none', color: '#7a8599', fontSize: '12px', cursor: 'pointer', fontFamily: 'Space Mono, monospace' }}
            >
              ← 다시 시작
            </button>
          </>
        )}

        {error && (
          <div style={{ background: 'rgba(255,51,102,0.07)', border: '1px solid rgba(255,51,102,0.2)', borderRadius: '8px', padding: '12px 14px', fontSize: '12px', color: '#ff3366', fontFamily: 'Space Mono, monospace', wordBreak: 'break-all' }}>
            ⚠ {error}
          </div>
        )}
      </div>
    </div>
  );
}
